import { AccessTokenDto, ResponseError } from '@cratehackers/api-client';
import * as dash from 'lodash';
import ReactGA from 'react-ga4';
import { config } from '../config';
import {
  AppIdentifiers,
  getToken,
  setToken,
  unsetToken,
} from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { iTrack, iTracks } from '../types/ITrack';
import { base64encode, sha256 } from '../utils/cryptographicFunctions';
import { generateRandomString } from '../utils/generateRandomString';
import isElectron from '../utils/isElectron';
import ApiService from './Api.service';
import RequestService from './Request.service';

export default class SpotifyService {
  /* App Configuration */
  private static REDIRECT_URI_ELECTRON = 'cratehackersx://spotify/callback';
  private static REDIRECT_URI_WEB = config.BASE_URL + '/spotify/callback';

  /* Crate Hackers API Configuration */
  private static appApiUrl =
    config.API_BASE_URL ?? 'https://api.cratehackers.com/'; // TODO: Replace with backend SDK

  /* Spotify API Configuration */
  private static CLIENT_ID = config.SPOTIFY_CLIENT_ID || '';
  // Separate client ID for Pipelines feature (uses main CLIENT_ID as fallback)
  private static PIPELINES_CLIENT_ID =
    config.PIPELINES_SPOTIFY_CLIENT_ID || config.SPOTIFY_CLIENT_ID || '';

  /* Rate Limiting Configuration */
  private static lastRequestTime = 0;
  private static minRequestInterval = 100; // Minimum 100ms between requests
  private static maxRetries = 3;
  private static baseRetryDelay = 1000; // 1 second base delay for exponential backoff

  private static SCOPES = [
    'user-read-private',
    'user-read-email',
    'playlist-modify-public',
    'playlist-modify-private',
    'playlist-read-private',
  ];
  private static apiUrl = 'https://api.spotify.com/v1';
  private static accountUrl = 'https://accounts.spotify.com';

  // One-time migration: clear stale Spotify tokens from the dev-mode app mixup.
  // Bump the version string if you ever need to force-clear again.
  private static readonly TOKEN_MIGRATION_KEY = 'spotify_token_migration';
  private static readonly TOKEN_MIGRATION_VERSION = '1';
  private static readonly _migrationDone = SpotifyService._runTokenMigration();
  private static _runTokenMigration(): boolean {
    try {
      const migrated = localStorage.getItem(SpotifyService.TOKEN_MIGRATION_KEY);
      if (migrated !== SpotifyService.TOKEN_MIGRATION_VERSION) {
        localStorage.removeItem('spotify');
        localStorage.removeItem('spotify_access_token');
        localStorage.removeItem('spotify_refresh_token');
        localStorage.removeItem('spotify_token_expiry');
        localStorage.removeItem('spotify_auth_client_id');
        localStorage.removeItem('spotify_client_id');
        localStorage.removeItem('spotify_code_verifier');
        localStorage.removeItem('spotify_redirect');
        localStorage.setItem(
          SpotifyService.TOKEN_MIGRATION_KEY,
          SpotifyService.TOKEN_MIGRATION_VERSION,
        );
        console.log('[Spotify] Cleared stale tokens (migration v1)');
      }
    } catch {
      // localStorage unavailable (SSR, etc.) -- skip silently
    }
    return true;
  }

  // Data structure that manages the current active token, caching it in localStorage
  public static getAccessToken = () => {
    return getToken(AppIdentifiers.Spotify).accessToken;
  };

  public static getRefreshToken() {
    return getToken(AppIdentifiers.Spotify).refreshToken; // TODO: Refactor token manager to support refresh and refresh_in values
  }

  public static getTokenExpiration() {
    return getToken(AppIdentifiers.Spotify).expiration;
  }

  public static getAuthCode() {
    // With hash router, query params are inside the hash: /#/path?code=...
    // So we need to parse from hash, not from window.location.search
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex);
      const args = new URLSearchParams(queryString);
      return args.get('code');
    }
    // Fallback to regular search params
    const args = new URLSearchParams(window.location.search);
    return args.get('code');
  }

  /**
   * Returns true if the error indicates a private/forbidden resource
   * that could be resolved by logging in to Spotify.
   */
  public static isPrivateResourceError(error: unknown): boolean {
    if (error instanceof ResponseError) {
      return error.response.status === 403;
    }
    const msg = (error as any)?.message ?? '';
    return msg.includes('403') || msg.includes('RESOURCE_PRIVATE');
  }

  /**
   * Build an AccessTokenDto from the stored Spotify credentials for
   * passing through to the backend API (which can use it for private playlists).
   */
  private static buildAccessTokenDto(): AccessTokenDto | undefined {
    const token = getToken(AppIdentifiers.Spotify);
    if (!token.accessToken) return undefined;
    return {
      accessToken: token.accessToken,
      refreshToken: token.refreshToken ?? '',
      expiresIn: token.expiration
        ? Math.max(0, Math.floor((token.expiration - Date.now()) / 1000))
        : 0,
      tokenType: 'Bearer',
    };
  }

  public static async getToken(code: string) {
    const code_verifier = localStorage.getItem('spotify_code_verifier') ?? '';
    // Use the client ID that was stored during authorization
    const clientId =
      localStorage.getItem('spotify_client_id') || this.CLIENT_ID;

    // The redirect_uri must match what was used in the authorization request
    // If using auth bridge, use the bridge URL; otherwise use direct URL
    let redirectUri: string;
    if (config.AUTH_BRIDGE_URL) {
      redirectUri = `${config.AUTH_BRIDGE_URL}/api/spotify/callback`;
    } else {
      // isElectron() is authoritative - don't check localhost
      redirectUri = isElectron()
        ? this.REDIRECT_URI_ELECTRON
        : this.REDIRECT_URI_WEB;
    }

    const response = await fetch(this.accountUrl + '/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: redirectUri,
        code_verifier: code_verifier,
      }),
    });

    localStorage.removeItem('spotify_code_verifier');
    // Keep the client_id - we need it for token refresh!
    // Move it to a permanent storage key
    const usedClientId = localStorage.getItem('spotify_client_id');
    if (usedClientId) {
      localStorage.setItem('spotify_auth_client_id', usedClientId);
      localStorage.removeItem('spotify_client_id');
    }

    return await response.json();
  }

  public static async refreshToken(forced?: boolean) {
    const tokenExpiration = this.getTokenExpiration() ?? 0;
    const refreshToken = this.getRefreshToken();
    if ((!forced && tokenExpiration > Date.now()) || !refreshToken) {
      return;
    }

    // Use the same client ID that was used during authorization
    const authClientId =
      localStorage.getItem('spotify_auth_client_id') || this.CLIENT_ID;

    try {
      const response = await fetch(this.accountUrl + '/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: authClientId,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok || responseJson.error) {
        const isAuthError =
          response.status === 400 ||
          responseJson.error === 'invalid_grant' ||
          responseJson.error === 'invalid_client';

        if (isAuthError) {
          console.error(
            '[Spotify] Token refresh failed (auth error):',
            responseJson,
          );
          getAppStore().dispatch(unsetToken(AppIdentifiers.Spotify));
          throw new Error('Spotify session expired. Please re-authenticate.');
        } else {
          console.warn('[Spotify] Token refresh failed:', responseJson);
          throw new Error(
            `Spotify API error: ${responseJson.error || response.statusText}`,
          );
        }
      }

      if (responseJson.access_token) {
        getAppStore().dispatch(
          setToken(
            AppIdentifiers.Spotify,
            responseJson.access_token,
            responseJson.refresh_token || refreshToken,
            Date.now() + Number(responseJson.expires_in) * 1000,
          ),
        );
      }

      return response;
    } catch (error) {
      console.error('[Spotify] Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Logout from Spotify - clears all stored tokens and credentials
   */
  public static logout(): void {
    console.log('[Spotify] Logging out - clearing all credentials');
    // Clear Redux store token
    getAppStore().dispatch(unsetToken(AppIdentifiers.Spotify));
    // Also clear any legacy localStorage items
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiry');
    localStorage.removeItem('spotify_code_verifier');
    localStorage.removeItem('spotify_client_id');
    localStorage.removeItem('spotify_auth_client_id');
    localStorage.removeItem('spotify_redirect');
  }

  public static async getSpotifyAuthorizeUrl(
    internalRedirectUri: string,
    usePipelinesCredentials = false,
  ) {
    const codeVerifier = generateRandomString(128);
    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64encode(hashed);

    // Use Pipelines client ID if requested and available
    const clientId = usePipelinesCredentials
      ? this.PIPELINES_CLIENT_ID
      : this.CLIENT_ID;

    // Determine the actual target for the callback
    // isElectron() is authoritative - localhost check was causing issues in dev mode
    const isDesktopApp = isElectron();
    // In dev mode (localhost), use localhost as target so auth-bridge redirects there
    // In production (packaged app), use custom protocol
    const isDevMode = window.location.hostname === 'localhost';
    const actualTarget =
      isDesktopApp && !isDevMode
        ? 'cratehackersx:/'
        : isDevMode
          ? 'http://localhost:8100'
          : window.location.origin;

    console.log(
      '[Spotify Auth] isDesktopApp:',
      isDesktopApp,
      'isDevMode:',
      isDevMode,
      'target:',
      actualTarget,
    );

    // Use auth bridge if configured, otherwise direct redirect
    let redirectUri: string;
    let stateParam: string;

    if (config.AUTH_BRIDGE_URL) {
      // Use the auth bridge - encode target in state
      redirectUri = `${config.AUTH_BRIDGE_URL}/api/spotify/callback`;
      const stateData = {
        target: actualTarget,
        originalState: internalRedirectUri,
      };
      stateParam = btoa(JSON.stringify(stateData));
      console.log(
        '[Spotify] Using auth bridge:',
        redirectUri,
        'target:',
        actualTarget,
      );
    } else {
      // Direct redirect (fallback)
      redirectUri = isDesktopApp
        ? this.REDIRECT_URI_ELECTRON
        : this.REDIRECT_URI_WEB;
      stateParam = internalRedirectUri;
      console.log('[Spotify] Using direct redirect:', redirectUri);
    }

    window.localStorage.setItem('spotify_code_verifier', codeVerifier);
    window.localStorage.setItem('spotify_redirect', internalRedirectUri);
    // Store which client ID was used for token exchange
    window.localStorage.setItem('spotify_client_id', clientId);

    const authUrl = new URL(this.accountUrl + '/authorize');
    const params: Record<string, string> = {
      response_type: 'code',
      client_id: clientId,
      scope: this.SCOPES.join(' '),
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      redirect_uri: redirectUri,
      state: stateParam,
      show_dialog: 'false',
    };

    authUrl.search = new URLSearchParams(params).toString();
    return authUrl.toString();
  }

  public static async getProfile() {
    await this.refreshToken();

    const response = await fetch(this.apiUrl + '/me', {
      headers: {
        Authorization: `Bearer ${getToken('spotify').accessToken}`,
      },
    });

    return await response.json();
  }

  public static async getPlaylists() {
    await this.refreshToken();
    const response = await fetch(this.apiUrl + '/me/playlists', {
      headers: {
        Authorization: `Bearer ${getToken('spotify').accessToken}`,
      },
    });
    return await response.json();
  }

  /**
   * Get playlist details including track count
   */
  public static async getPlaylist(
    playlistId: string,
  ): Promise<{ id: string; name?: string; tracks?: { total: number } } | null> {
    try {
      await this.refreshToken();
      const response = await fetch(`${this.apiUrl}/playlists/${playlistId}`, {
        headers: {
          Authorization: `Bearer ${getToken('spotify').accessToken}`,
        },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error('[Spotify] Error fetching playlist:', error);
      return null;
    }
  }

  public static async getPlaylistTracks(playlistId: string): Promise<iTrack[]> {
    await this.refreshToken();
    const response = await fetch(
      `${this.apiUrl}/playlists/${playlistId}/tracks`,
      {
        headers: {
          Authorization: `Bearer ${getToken('spotify').accessToken}`,
        },
      },
    );
    const data = await response.json();

    if (!data?.items) {
      return [];
    }

    return data.items
      .filter((item: any) => item.track || item.item)
      .map((item: any) => {
        const itm = item.track || item.item;
        return {
          ID: itm.id,
          Artist: itm.artists?.map((a: any) => a.name).join(', ') || 'Unknown',
          Title: itm.name || 'Unknown',
          Album: itm.album?.name || undefined,
          AlbumCover: itm.album?.images?.[0]?.url || '',
          duration: itm.duration_ms || undefined,
          isrc: itm.external_ids?.isrc || undefined,
          BPM: undefined,
          Key_Camelot: undefined,
          PreviewURL: itm.preview_url || undefined,
        };
      });
  }

  public static async SearchTracks(query: string): Promise<iTracks> {
    await this.refreshToken();

    let options;
    const tokenExpiration = getToken('spotify').accessToken ?? null;
    if (tokenExpiration) {
      options = {
        headers: {
          Authorization: `Bearer ${getToken('spotify').accessToken}`,
        },
      };
    }

    const response = await fetch(
      `${this.apiUrl}/search?q=${query}&type=track`,
      options,
    );

    return await this.mapSpotifyResponse(await response.json(), true);
  }

  public static async ExportCrateToSpotify(
    crateName: string,
    tracks: iTrack[],
    isCancelledRef?: { current: boolean },
    onProgress?: (
      current: number,
      total: number,
      currentTrack?: string,
      status?: string,
    ) => void,
  ): Promise<{
    success: boolean;
    successTracks: iTrack[];
    failedTracks: iTrack[];
  }> {
    const result = {
      success: false,
      successTracks: [] as iTrack[],
      failedTracks: [] as iTrack[],
    };

    try {
      if (isCancelledRef?.current) {
        return result;
      }
      onProgress?.(0, tracks.length, 'Creating playlist...', 'creating');

      const playlistData = await this.createPlaylist(crateName);
      const playlistId = playlistData.id;

      const trackChunks = dash.chunk(tracks, 100); // split the tracks array into chunks of 100

      let totalProcessed = 0;
      for (const [chunkIndex, chunk] of trackChunks.entries()) {
        if (isCancelledRef?.current) {
          return result;
        }

        const trackURIs: string[] = [];

        // Process each track in the chunk
        for (const [trackIndex, track] of chunk.entries()) {
          if (isCancelledRef?.current) {
            return result;
          }
          const currentOverallIndex = totalProcessed + trackIndex;
          onProgress?.(
            currentOverallIndex,
            tracks.length,
            `${track.Artist} - ${track.Title}`,
            'searching',
          );

          try {
            let newTitle = '';
            if (track.Title) {
              const match = track.Title.match(/^[^(\[{-]+/);
              newTitle = match ? match[0].trim() : track.Title.trim();
            }

            const searchResult = await this.SearchTracks(
              `${track.Artist} ${newTitle}`,
            );

            if (searchResult?.tracks && searchResult.tracks.length > 0) {
              trackURIs.push(`spotify:track:${searchResult.tracks[0].ID}`);
              result.successTracks.push(track);
            } else {
              result.failedTracks.push(track);
            }
          } catch (searchError) {
            console.error(
              `Error searching for track ${track.Artist} - ${track.Title}:`,
              searchError,
            );
            result.failedTracks.push(track);
          }
        }
        totalProcessed += chunk.length;

        // Only add tracks to playlist if we have valid URIs
        if (trackURIs.length > 0) {
          if (isCancelledRef?.current) {
            return result;
          }
          onProgress?.(
            totalProcessed,
            tracks.length,
            `Adding ${trackURIs.length} tracks to playlist...`,
            'adding',
          );

          try {
            const response = await this.callApi(
              `/playlists/${playlistId}/tracks`,
              'POST',
              {
                uris: trackURIs,
              },
            );
            console.log(`Added ${trackURIs.length} tracks to playlist`);
          } catch (addTracksError) {
            console.error('Error adding tracks to playlist:', addTracksError);
            // If adding tracks fails, move successful tracks back to failed
            const failedFromThisChunk = result.successTracks.splice(
              -trackURIs.length,
            );
            result.failedTracks.push(...failedFromThisChunk);
          }
        }
      }
      onProgress?.(
        tracks.length,
        tracks.length,
        'Export completed!',
        'completed',
      );

      // Consider export successful if we have any successful tracks
      result.success = result.successTracks.length > 0;

      ReactGA.event({
        category: 'Export',
        action: 'Export Crate',
        label: 'spotify',
        value: result.success ? 1 : 0,
      });
    } catch (error) {
      console.error('Error in ExportCrateToSpotify:', error);
      onProgress?.(0, tracks.length, 'Export failed', 'error');
      // If playlist creation fails, all tracks are considered failed
      result.failedTracks = [...tracks];
      result.success = false;
    }

    return result;
  }

  public static async GetRecommendations(
    track: iTrack,
  ): Promise<{ tracks: any; info: any }> {
    const filters = `${
      track.ArtistID ? `seed_artist=${track.ArtistID.split(',')[0]}&` : ''
    }seed_tracks=${track.ID}`;
    await this.refreshToken();

    let options;
    const tokenExpiration = getToken('spotify').accessToken ?? null;
    if (tokenExpiration) {
      options = {
        headers: {
          Authorization: `Bearer ${getToken('spotify').accessToken}`,
        },
      };
    }

    const response = await fetch(
      `${this.apiUrl}/recommendations?limit=20&${filters}`,
      options,
    );

    const resData = await response.json();
    const tracks: any = await this.mapSpotifyResponse(resData);

    return {
      tracks,
      info: resData.seeds[0],
    };
  }

  public static async GetPlaylistById(playlistId: string, i: number) {
    await this.refreshToken();

    const limit = 100;
    const offset = i * limit;
    const api = ApiService.getApiClient();
    const token = this.buildAccessTokenDto();

    const response: any = await api.spotifyControllerGetPlaylistTracks({
      myPlaylistIdRequestDto: { playlistId, limit, offset, token },
    });

    const tracks: any = await this.mapSpotifyResponse(response);

    return {
      tracks,
      info: response,
    };
  }
  public static async GetPlaylistInfoById(playlistId: string) {
    await this.refreshToken();

    const api = ApiService.getApiClient();
    const token = this.buildAccessTokenDto();

    const response: any = await api.spotifyControllerGetPlaylistInfo({
      myPlaylistInfoIdRequestDto: { playlistId, token },
    });

    const tracks: any = await this.mapSpotifyResponse(response);

    return {
      tracks,
      info: response,
    };
  }

  public static async GetAlbumById(
    albumId: string,
    i: number,
    albumCover: string,
  ) {
    await this.refreshToken();

    const limit = 50;
    const offset = i * limit;
    const api = ApiService.getApiClient();
    const token = this.buildAccessTokenDto();

    const response: any = await api.spotifyControllerGetAlbumTracks({
      myAlbumIdRequestDto: { albumId, limit, offset, token },
    });

    const tracks: any = await this.mapSpotifyResponse(
      response,
      false,
      albumCover,
    );

    return {
      tracks,
      info: response,
    };
  }

  public static async GetAlbumInfoById(albumId: string) {
    await this.refreshToken();

    const api = ApiService.getApiClient();
    const token = this.buildAccessTokenDto();

    const response: any = await api.spotifyControllerGetAlbumInfo({
      myAlbumInfoIdRequestDto: { albumId, token },
    });

    const tracks: any = await this.mapSpotifyResponse(response);

    return {
      tracks,
      info: response,
    };
  }

  public static async createPlaylist(
    name: string,
    description?: string,
  ): Promise<any> {
    // Sanitize playlist name - be aggressive about removing problematic characters
    // Replace smart quotes and em dashes with standard versions first
    let sanitizedName = (name || 'Crate Hackers Playlist')
      .replace(/[""]/g, '"') // Smart double quotes to straight
      .replace(/['']/g, "'") // Smart single quotes to straight
      .replace(/[—–]/g, '-') // Em/en dashes to hyphen
      .replace(/[^\x20-\x7E]/g, '') // Remove all non-ASCII printable chars
      .substring(0, 100) // Spotify name limit
      .trim();

    // Ensure name is not empty after sanitization
    if (!sanitizedName) {
      sanitizedName = 'Crate Hackers Playlist';
    }

    // Sanitize description - Spotify has a 300 character limit
    const defaultDescription = `Playlist created by Crate Hackers`;
    const sanitizedDescription = (description || defaultDescription)
      .replace(/[""]/g, '"') // Smart double quotes to straight
      .replace(/['']/g, "'") // Smart single quotes to straight
      .replace(/[—–]/g, '-') // Em/en dashes to hyphen
      .replace(/[^\x20-\x7E]/g, '') // Remove all non-ASCII printable chars
      .substring(0, 300) // Spotify description limit
      .trim();

    console.log('[Spotify] Sanitized name:', sanitizedName);
    console.log('[Spotify] Sanitized description:', sanitizedDescription);

    const body = {
      name: sanitizedName,
      description: sanitizedDescription,
      public: false,
    };

    console.log('[Spotify] Creating playlist:', {
      nameLength: sanitizedName.length,
      descLength: sanitizedDescription.length,
      body,
    });

    try {
      // Only refresh if token is actually expired (don't force)
      await this.refreshToken();
      const accessToken = this.getAccessToken();

      if (!accessToken) {
        throw new Error('No Spotify access token available. Please reconnect.');
      }

      console.log('[Spotify] Making playlist request:', {
        hasToken: true,
        tokenLength: accessToken.length,
      });

      const response = await fetch(`${this.apiUrl}/me/playlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Spotify] Playlist creation failed:', {
          status: response.status,
          statusText: response.statusText,
          errorFull: JSON.stringify(errorData, null, 2),
        });
        const errorMessage =
          errorData?.error?.message ||
          errorData?.error_description ||
          `Spotify API error (${response.status})`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error: any) {
      // Check if this is a scope/permission issue
      if (
        error.message?.toLowerCase().includes('bad request') ||
        error.message?.toLowerCase().includes('forbidden')
      ) {
        console.error(
          '[Spotify] Playlist creation failed - this may be a token scope issue.',
          'Try disconnecting and reconnecting to Spotify to get fresh permissions.',
        );
        throw new Error(
          'Failed to create playlist. Please disconnect from Spotify (right-click the button) and reconnect to refresh your permissions.',
        );
      }
      throw error;
    }
  }

  /**
   * Delete a Spotify playlist (unfollow it)
   * Note: Spotify API uses "unfollow" to delete playlists you own
   */
  public static async deletePlaylist(playlistId: string): Promise<boolean> {
    try {
      console.log('[Spotify] Deleting playlist:', playlistId);
      const response = await fetch(
        `${this.apiUrl}/playlists/${playlistId}/followers`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${this.getAccessToken()}`,
          },
        },
      );

      if (response.ok || response.status === 200 || response.status === 204) {
        console.log('[Spotify] Playlist deleted successfully');
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Spotify] Failed to delete playlist:', {
          status: response.status,
          error: errorData,
        });
        return false;
      }
    } catch (error) {
      console.error('[Spotify] Error deleting playlist:', error);
      return false;
    }
  }

  public static async addTracksToPlaylist(
    playlistId: string,
    uris: string[],
  ): Promise<boolean> {
    // Spotify API limit: max 100 tracks per request
    const BATCH_SIZE = 100;

    if (uris.length === 0) {
      console.warn(
        '[Spotify] addTracksToPlaylist called with empty uris array',
      );
      return false;
    }

    // Process in batches of 100
    for (let i = 0; i < uris.length; i += BATCH_SIZE) {
      const batch = uris.slice(i, i + BATCH_SIZE);
      console.log(
        `[Spotify] Adding tracks batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(uris.length / BATCH_SIZE)} (${batch.length} tracks)`,
      );

      const response = await this.callApi(
        `/playlists/${playlistId}/tracks`,
        'POST',
        {
          uris: batch,
        },
      );

      if (response.snapshot_id === undefined) {
        console.error('[Spotify] Failed to add tracks batch:', response);
        return false;
      }
    }

    return true;
  }

  /**
   * Replace all tracks in a playlist (for sync updates)
   */
  public static async replacePlaylistTracks(
    playlistId: string,
    uris: string[],
  ): Promise<boolean> {
    // Spotify's PUT endpoint replaces all tracks
    // But it only accepts up to 100 tracks at once
    // For more, we need to PUT first 100, then POST the rest
    const firstBatch = uris.slice(0, 100);
    const remaining = uris.slice(100);

    const response = await this.callApi(
      `/playlists/${playlistId}/tracks`,
      'PUT',
      {
        uris: firstBatch,
      },
    );

    if (response.snapshot_id && remaining.length > 0) {
      // Add remaining tracks in batches
      for (let i = 0; i < remaining.length; i += 100) {
        const batch = remaining.slice(i, i + 100);
        await this.callApi(`/playlists/${playlistId}/tracks`, 'POST', {
          uris: batch,
        });
      }
    }

    return response.snapshot_id !== undefined;
  }

  private static async addTrackToPlaylist(
    playlistId: string,
    trackId: string,
  ): Promise<boolean> {
    const response = await this.callApi(
      `/playlists/${playlistId}/tracks`,
      'POST',
      {
        uris: [`spotify:track:${trackId}`],
      },
    );
    return response.snapshot_id !== undefined;
  }

  private static async callAppApi(url: string, method: string, body: any) {
    console.log('callAppApi url:', url);
    console.log('callAppApi method:', method);

    const accessToken = this.getAccessToken() ?? null;
    if (accessToken) {
      if (typeof body.token === 'undefined') {
        body.token = getToken(AppIdentifiers.Spotify);
        body.token.token_type = 'Bearer';
      }
    }

    console.log('callAppApi body:', body);

    url = this.appApiUrl + url;
    return await RequestService.fetch({ url, method, body });
  }

  /**
   * Delay helper for rate limiting
   */
  private static delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Enforces minimum interval between API requests to avoid rate limiting
   */
  private static async rateLimitDelay(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.minRequestInterval) {
      await this.delay(this.minRequestInterval - timeSinceLastRequest);
    }
    this.lastRequestTime = Date.now();
  }

  private static async callApi(
    url: string,
    method = 'GET',
    body?: any,
    retryCount = 0,
  ): Promise<any> {
    // Apply rate limiting delay before making request
    await this.rateLimitDelay();

    const accessToken = this.getAccessToken() ?? null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const options: RequestInit = {
      method: method,
      headers: headers,
    };

    // Only add body for non-GET requests
    if (body !== undefined && method !== 'GET') {
      options.body = this.buildRequestBody(body);
    }

    // Debug logging for troubleshooting
    console.log('[Spotify API Request]', {
      url: this.apiUrl + url,
      method,
      hasAuth: !!accessToken,
      bodyPreview: body ? JSON.stringify(body).substring(0, 200) : null,
      retryCount,
    });

    let response: Response;
    try {
      response = await fetch(this.apiUrl + url, options);
    } catch (fetchError: any) {
      const isNetworkError =
        fetchError instanceof TypeError ||
        fetchError.message?.includes('fetch') ||
        fetchError.message?.includes('NetworkError');
      if (isNetworkError && retryCount < this.maxRetries) {
        const delayMs = this.baseRetryDelay * Math.pow(2, retryCount);
        console.warn(
          `[Spotify API] Network error, retrying in ${delayMs}ms (attempt ${retryCount + 1}/${this.maxRetries})...`,
          fetchError,
        );
        await this.delay(delayMs);
        return this.callApi(url, method, body, retryCount + 1);
      }
      throw fetchError;
    }

    // Handle rate limiting (429 Too Many Requests)
    if (response.status === 429) {
      if (retryCount >= this.maxRetries) {
        throw new Error(
          'Spotify rate limit exceeded. Please wait a moment and try again.',
        );
      }

      // Get retry delay from Retry-After header, or use exponential backoff
      const retryAfter = response.headers.get('Retry-After');
      let delayMs: number;

      if (retryAfter) {
        // Retry-After is in seconds
        delayMs = parseInt(retryAfter, 10) * 1000;
        console.log(
          `[Spotify Rate Limit] Retry-After header: ${retryAfter}s, waiting ${delayMs}ms`,
        );
      } else {
        // Exponential backoff: 1s, 2s, 4s, etc.
        delayMs = this.baseRetryDelay * Math.pow(2, retryCount);
        console.log(
          `[Spotify Rate Limit] No Retry-After header, using exponential backoff: ${delayMs}ms`,
        );
      }

      await this.delay(delayMs);
      return this.callApi(url, method, body, retryCount + 1);
    }

    if (
      response.status === 502 ||
      response.status === 503 ||
      response.status === 504
    ) {
      if (retryCount < this.maxRetries) {
        const delayMs = this.baseRetryDelay * Math.pow(2, retryCount);
        console.warn(
          `[Spotify API] Transient error ${response.status}, retrying in ${delayMs}ms (attempt ${retryCount + 1}/${this.maxRetries})...`,
        );
        await this.delay(delayMs);
        return this.callApi(url, method, body, retryCount + 1);
      }
    }

    // If Unauthorized, try refreshing token or re-authenticating
    if (response.ok) {
      return response.json();
    } else if (response.status === 401) {
      await this.refreshToken();
      const newAccessToken = this.getAccessToken() ?? null;
      if (newAccessToken) {
        headers.Authorization = `Bearer ${newAccessToken}`;
        const newResponse = await fetch(this.apiUrl + url, options);
        if (newResponse.ok) {
          return newResponse.json();
        } else {
          throw new Error('Reauthorization needed.');
        }
      } else {
        throw new Error('Reauthorization needed.');
      }
    } else {
      // Try to get the actual error message from Spotify
      let errorMessage = `Spotify API error (${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody?.error?.message) {
          errorMessage = errorBody.error.message;
        } else if (errorBody?.error_description) {
          errorMessage = errorBody.error_description;
        } else if (response.status === 400) {
          // Provide more context for Bad Request errors
          errorMessage = `Bad Request on ${url}. This may indicate invalid data or exceeding API limits.`;
        }
        console.error(
          '[Spotify API Error]',
          response.status,
          url,
          JSON.stringify(errorBody, null, 2),
        );
      } catch {
        // If we can't parse the error body, use the status text
        errorMessage = response.statusText || errorMessage;
        console.error(
          '[Spotify API Error] Failed to parse error body:',
          response.status,
          url,
        );
      }
      throw new Error(errorMessage);
    }
  }

  private static buildRequestBody(data: any): BodyInit | null {
    if (data === null || data === undefined) {
      return null;
    }

    try {
      return JSON.stringify(data);
    } catch (error) {
      console.error('Error serializing request body to JSON:', error);
      console.error('Data that failed to serialize:', data);
      throw new Error('Failed to serialize request data to JSON');
    }
  }

  private static async mapSpotifyResponse(
    response: any,
    simple = false,
    albumCoverPicture = 'defaultImageUrl',
  ): Promise<iTracks> {
    // Handle undefined or empty response
    if (!response) {
      console.warn('[mapSpotifyResponse] Response is undefined');
      return { tracks: [] } as unknown as iTracks;
    }

    const items = response.tracks?.items || response.tracks || response.items;

    if (!items || !Array.isArray(items) || items.length === 0) {
      console.warn('[mapSpotifyResponse] No items found in response');
      return { tracks: [] } as unknown as iTracks;
    }

    // Collect all track IDs for bulk fetching
    const trackIds: string[] = items
      .map((item: any) => (item.track || item.item || item).id)
      .filter((id: string) => id);

    // Fetch hydrated data in bulk (much more efficient than individual calls)
    let hydratedDataMap = new Map<string, any>();
    if (!simple && trackIds.length > 0) {
      console.log(
        `[mapSpotifyResponse] Fetching ${trackIds.length} tracks in bulk`,
      );
      try {
        hydratedDataMap = await ApiService.getBulkTracks(trackIds);
        console.log(
          `[mapSpotifyResponse] Successfully hydrated ${hydratedDataMap.size} tracks`,
        );
      } catch (error) {
        console.error('[mapSpotifyResponse] Bulk hydration failed:', error);
        // Continue without hydrated data rather than failing completely
      }
    }

    // Map items with hydrated data
    const trackItems = items.map((item: any) => {
      const itm = item.track || item.item || item;
      const hydratedItem = hydratedDataMap.get(itm.id);

      const albumCover =
        itm.album?.images[0]?.url ||
        response?.images?.[0]?.url ||
        itm.images?.[0]?.url ||
        albumCoverPicture;

      return {
        ID: itm.id,
        Artist:
          itm.artists?.map((artist: any) => artist.name).join(', ') ||
          'Unknown',
        Title: itm.name,
        SpotifyID: itm.id,
        Preview: itm.preview_url,
        AlbumCover: albumCover,
        duration: SpotifyService.formatDuration(itm.duration_ms),
        isrc: itm.external_ids?.isrc || undefined,
        // Use hydrated data - bulk endpoint already returns percentages
        energy: hydratedItem?.energy,
        danceability: hydratedItem?.danceability,
        valence: hydratedItem?.valence,
        popularity: hydratedItem?.popularity,
        Key_Camelot: hydratedItem?.key,
        BPM: hydratedItem?.bpm,
      };
    });

    return { tracks: trackItems } as unknown as iTracks;
  }

  private static formatDuration(durationMs: number): string {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = ((durationMs % 60000) / 1000).toFixed(0);
    return minutes + ':' + (parseInt(seconds) < 10 ? '0' : '') + seconds;
  }

  /**
   * Fetches album artwork for a track from Spotify
   * @param artist The track artist
   * @param title The track title
   * @returns URL to the album artwork or null if not found
   */
  public static async getTrackAlbumArtwork(
    artist: string,
    title: string,
  ): Promise<string | null> {
    try {
      // Clean the title to remove remix tags, etc.
      let cleanTitle = title;
      const match = title.match(/^[^(\[{-]+/);
      if (match) {
        cleanTitle = match[0].trim();
      }

      // Try direct Spotify API search first
      try {
        const searchResult = await this.SearchTracks(`${artist} ${cleanTitle}`);

        if (searchResult?.tracks && searchResult.tracks.length > 0) {
          return searchResult.tracks[0].AlbumCover || null;
        }
      } catch (spotifyError) {
        console.log(
          'Direct Spotify search failed, using app API:',
          spotifyError,
        );
      }

      // Fall back to app API if Spotify authentication fails
      try {
        const response = await this.callAppApi(`/spotify/search`, 'POST', {
          query: `${artist} ${cleanTitle}`,
          type: 'track',
        });

        if (response?.tracks?.items && response.tracks.items.length > 0) {
          const track = response.tracks.items[0];
          if (track.album?.images && track.album.images.length > 0) {
            return track.album.images[0].url;
          }
        }
      } catch (appApiError) {
        console.error('App API search also failed:', appApiError);
      }

      // Last resort: Try using a public album art API
      try {
        // Use Last.fm API through proxy service to avoid CORS issues
        const encodedArtist = encodeURIComponent(artist);
        const encodedTrack = encodeURIComponent(cleanTitle);
        const url = `https://ws.audioscrobbler.com/2.0/?method=track.getInfo&api_key=YOUR_LASTFM_KEY&artist=${encodedArtist}&track=${encodedTrack}&format=json`;

        // Since we don't have a real API key, return a static placeholder image that looks like album art
        // In a real implementation, we would parse the Last.fm response and extract the album art URL
        return `https://picsum.photos/300/300?random=${Math.floor(Math.random() * 1000)}`;
      } catch (lastFmError) {
        console.error('Last.fm fallback also failed:', lastFmError);
      }

      return null;
    } catch (error) {
      console.error('Error fetching Spotify album artwork:', error);
      return null;
    }
  }
}
